import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Typography,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";

import { useCustomNotification } from "design-system/hooks";
import { Priority, useFeatureRequestMutation } from "generated/graphql";

import s from "./FeatureRequest.module.css";

const { Title, Text } = Typography;
const { Content } = Layout;

interface FeatureRequestFormValues {
  description?: string;
  emailAddress: string;
  name: string;
  priority: Priority;
  problem: string;
}

export const FeatureRequest = () => {
  const { t } = useTranslation(["common"]);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [featureRequestMutation] = useFeatureRequestMutation();
  const { user } = useAuth();
  const { showNotification } = useCustomNotification();

  useEffect(() => {
    if (user) {
      const name =
        `${user.profile?.given_name} ${user.profile?.family_name}`.trim();
      form.setFieldsValue({
        name,
        emailAddress: user.profile?.email ?? "",
      });
    }
  }, [user, form]);

  const handleSubmit = useCallback(
    async (values: FeatureRequestFormValues) => {
      setIsSubmitting(true);

      try {
        const result = await featureRequestMutation({
          variables: {
            input: values,
          },
        });

        if (result.data?.sendFeatureRequest) {
          showNotification({
            type: "success",
            message: t("components.featureRequest.successTitle"),
            description: t("components.featureRequest.successDescription"),
          });

          form.resetFields();
        } else {
          throw new Error(t("components.featureRequest.errorDescription"));
        }
      } catch (_error) {
        showNotification({
          type: "error",
          message: t("components.featureRequest.errorTitle"),
          description: t("components.featureRequest.errorDescription"),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [featureRequestMutation, form, showNotification, t],
  );

  return (
    <Layout className={s.container}>
      <Content className={s.content}>
        <Title level={1} className={s.title}>
          {t("components.featureRequest.title")}
        </Title>
        <Text className={s.subtitle}>
          {t("components.featureRequest.subtitle")}
        </Text>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className={s.form}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="name"
                label={t("labels.nameLabel")}
                rules={[
                  {
                    required: true,
                    message: t("components.featureRequest.nameRequired"),
                  },
                ]}
              >
                <Input
                  data-testid="feature-request-name"
                  placeholder={t("components.featureRequest.namePlaceholder")}
                  readOnly
                  className={s.input}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="emailAddress"
                label={t("labels.emailAddress")}
                rules={[
                  {
                    required: true,
                    message: t("labels.emailRequired"),
                  },
                  {
                    type: "email",
                    message: t("labels.emailInvalid"),
                  },
                ]}
              >
                <Input
                  data-testid="feature-request-email"
                  placeholder={t("components.featureRequest.emailPlaceholder")}
                  readOnly
                  className={s.input}
                />
              </Form.Item>
            </Col>
            <Col span={8} />
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="priority"
                label={t("components.featureRequest.priorityLabel")}
                rules={[
                  {
                    required: true,
                    message: t("components.featureRequest.priorityRequired"),
                  },
                ]}
              >
                <Select
                  placeholder={t(
                    "components.featureRequest.priorityPlaceholder",
                  )}
                  className={s.input}
                  data-testid="feature-request-priority"
                >
                  <Select.Option
                    value={Priority.Minor}
                    data-testid="feature-request-priority-minor"
                  >
                    {t("components.featureRequest.priorityMinor")}
                  </Select.Option>
                  <Select.Option
                    value={Priority.Major}
                    data-testid="feature-request-priority-major"
                  >
                    {t("components.featureRequest.priorityMajor")}
                  </Select.Option>
                  <Select.Option
                    value={Priority.Critical}
                    data-testid="feature-request-priority-critical"
                  >
                    {t("components.featureRequest.priorityCritical")}
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8} />
            <Col span={8} />
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="problem"
                label={t("components.featureRequest.problemLabel")}
                rules={[
                  {
                    required: true,
                    message: t("components.featureRequest.problemRequired"),
                  },
                ]}
              >
                <Input.TextArea
                  data-testid="feature-request-problem"
                  rows={4}
                  placeholder={t(
                    "components.featureRequest.problemPlaceholder",
                  )}
                  className={s.input}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="description"
                label={t("components.featureRequest.descriptionLabel")}
              >
                <Input.TextArea
                  data-testid="feature-request-description"
                  rows={4}
                  placeholder={t(
                    "components.featureRequest.descriptionPlaceholder",
                  )}
                  className={s.input}
                />
              </Form.Item>
            </Col>
            <Col span={8} />
          </Row>

          <Typography.Text className={s.note}>
            <span>* </span>
            {t("components.featureRequest.note")}
          </Typography.Text>

          <Divider />

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  data-testid="feature-request-submit"
                  block
                >
                  {t("components.featureRequest.submit")}
                </Button>
              </Form.Item>
            </Col>
            <Col span={16} />
          </Row>
        </Form>
      </Content>
    </Layout>
  );
};
